import React, { createContext, useContext } from "react";
import { useAuth0 } from "../auth/react-auth0-spa";
import {
  invokeApiOperation,
  cloneDateTimeAndAppendSecondsOffset,
  encodeUriString,
} from "./common/ApiHelper";
import { auditLogEntryFieldNames } from "../constants/model";

const AuditApiContext = createContext();
const HttpMethod = {
  Get: "GET",
};
const limitMetadata = Object.freeze({
  min: 1,
  max: 200,
});
const DefaultLimit = 15;

export const AuditApiProvider = (props) => {
  const { getTokenSilently } = useAuth0();

  const fetchUserAuditLogs = async (
    userId,
    dateTimeAfterUtc,
    dateTimeBeforeUtc,
    limit = DefaultLimit,
    continuationToken = null
  ) => {
    const sanitizedUserId = userId && userId.length > 0 ? userId.trim() : "";
    if (sanitizedUserId.length === 0) throw new Error("Invalid user Id");

    return await fetchAuditLogs(
      auditLogEntryFieldNames.subjectId,
      sanitizedUserId,
      dateTimeAfterUtc,
      dateTimeBeforeUtc,
      limit,
      continuationToken
    );
  };

  const fetchAuditLogs = async (
    searchBy,
    searchValue,
    dateTimeAfterUtc,
    dateTimeBeforeUtc,
    limit = DefaultLimit,
    continuationToken = null
  ) => {
    if (limit < limitMetadata.min || limit > limitMetadata.max)
      throw new Error("Invalid page size");

    const sanitizedSearchTerm = encodeUriString(searchValue);
    const sanitizedSearchParam =
      searchBy &&
      searchBy.length > 0 &&
      sanitizedSearchTerm &&
      sanitizedSearchTerm.length > 0
        ? `&${searchBy}=${sanitizedSearchTerm}`
        : "";
    const sanitizedAfterParam = getSanitizedDateParam(
      "dateTimeAfterUtc",
      cloneDateTimeAndAppendSecondsOffset(dateTimeAfterUtc, -1)
    );
    const sanitizedBeforeParam = getSanitizedDateParam(
      "dateTimeBeforeUtc",
      cloneDateTimeAndAppendSecondsOffset(dateTimeBeforeUtc, 1)
    );
    const continuationTokenParam = getSanitizedContinuationTokenParam(
      continuationToken
    );

    let urlSuffix =
      `auditlogs?limit=${limit}${sanitizedAfterParam}` +
      `${sanitizedBeforeParam}${sanitizedSearchParam}${continuationTokenParam}`;

    return await invokeApi(urlSuffix, HttpMethod.Get, true);
  };

  const getSanitizedContinuationTokenParam = (token) => {
    return token && token.length > 0
      ? `&continuationToken=${encodeUriString(token)}`
      : "";
  };

  const getSanitizedDateParam = (paramName, date) => {
    return `&${paramName}=${date.toJSON()}`;
  };

  const invokeApi = async (
    urlSuffix,
    httpMethod,
    parseResponseToJson,
    bodyData = null
  ) => {
    const token = await getTokenSilently();
    return await invokeApiOperation(
      token,
      urlSuffix,
      httpMethod,
      parseResponseToJson,
      bodyData
    );
  };

  const value = {
    fetchAuditLogs: props.fetchAuditLogs || fetchAuditLogs,
    fetchUserAuditLogs: props.fetchUserAuditLogs || fetchUserAuditLogs,
  };

  return (
    <AuditApiContext.Provider value={value}>
      {props.children}
    </AuditApiContext.Provider>
  );
};

export const useAuditApi = () => {
  return useContext(AuditApiContext);
};
